import http from '../http-common';
import { withRunParam } from './analysisRunOptions';

const API_URL_PATH = 'interviews/analysis/';

class InterviewAnalysisDataService {
  // ============================================================================
  // NEW SPLIT ENDPOINTS (optimized for large data)
  // ============================================================================

  /**
   * Get metadata only (videoUrl, interviewInfo, status, config)
   * Response size: ~2KB
   * @param {number} analysisId - The analysis/interview ID
   * @param {boolean} isManual - Whether this is a manual analysis
   * @param {string|null} run - Optional run id (`?run=`); omitted -> active run
   * @returns {Promise} - Metadata
   */
  getMetadata(analysisId, isManual = false, run = null) {
    return http.get(`${API_URL_PATH}${analysisId}/metadata`, {
      params: withRunParam({ isManual }, run),
    });
  }

  /**
   * Get gestures only
   * Response size: ~2-5MB
   * @param {number} analysisId - The analysis/interview ID
   * @param {boolean} isManual - Whether this is a manual analysis
   * @param {string|null} run - Optional run id (`?run=`); omitted -> active run
   * @returns {Promise} - Gesture analysis data
   */
  getGestures(analysisId, isManual = false, run = null) {
    return http.get(`${API_URL_PATH}${analysisId}/gestures`, {
      params: withRunParam({ isManual }, run),
    });
  }

  /**
   * Get transcription only
   * Response size: ~50-500KB
   * @param {number} analysisId - The analysis/interview ID
   * @param {boolean} isManual - Whether this is a manual analysis
   * @param {string|null} run - Optional run id (`?run=`); omitted -> active run
   * @returns {Promise} - Transcription data
   */
  getTranscription(analysisId, isManual = false, run = null) {
    return http.get(`${API_URL_PATH}${analysisId}/transcription`, {
      params: withRunParam({ isManual }, run),
    });
  }

  /**
   * Get landmarks by time window (paginated)
   * Response size: ~1-3MB per 15s window
   * @param {number} analysisId - The analysis/interview ID
   * @param {number} startTime - Start time in seconds
   * @param {number} endTime - End time in seconds
   * @param {boolean} isManual - Whether this is a manual analysis
   * @param {boolean} forceFresh - Bypass the HTTP cache
   * @param {string|null} run - Optional run id (`?run=`); omitted -> active run
   * @returns {Promise} - Landmarks data for the time window
   */
  getLandmarks(analysisId, startTime, endTime, isManual = false, forceFresh = false, run = null) {
    const params = withRunParam({ startTime, endTime, isManual }, run);
    // If forceFresh is true, add a cache-busting parameter to bypass HTTP cache
    if (forceFresh) {
      params._t = Date.now();
    }
    return http.get(`${API_URL_PATH}${analysisId}/landmarks`, {
      params,
      timeout: 15000,
      // Force axios to not use HTTP cache if we need fresh data
      headers: forceFresh ? { 'Cache-Control': 'no-cache' } : {},
    });
  }

  /**
   * Get beliefs analysis (only for regular interviews, not manual)
   * Response size: ~1-3MB
   * @param {number} analysisId - The analysis/interview ID
   * @param {boolean} isManual - Whether this is a manual analysis
   * @param {string|null} run - Optional run id (`?run=`); omitted -> active run
   * @returns {Promise} - Beliefs analysis data
   */
  getBeliefs(analysisId, isManual = false, run = null) {
    return http.get(`${API_URL_PATH}${analysisId}/beliefs`, {
      params: withRunParam({ isManual }, run),
    });
  }

  /**
   * Get persisted coherence UI preferences for current user and analysis.
   * @param {number} analysisId
   * @param {boolean} isManual
   * @returns {Promise}
   */
  getCoherencePreferences(analysisId, isManual = false) {
    return http.get(`${API_URL_PATH}${analysisId}/coherence-preferences`, {
      params: { isManual },
    });
  }

  /**
   * Persist coherence UI preferences for current user and analysis.
   * @param {number} analysisId
   * @param {{ segmentationTab: 'merged'|'per_alert'|'idea'|'none', scoreMode: 'alert_major'|'computed_subtraction', fontScale: number }} payload
   * @param {boolean} isManual
   * @returns {Promise}
   */
  setCoherencePreferences(analysisId, payload, isManual = false) {
    return http.put(`${API_URL_PATH}${analysisId}/coherence-preferences`, payload, {
      params: { isManual },
    });
  }

  // ============================================================================
  // LEGACY ENDPOINT (deprecated - use split endpoints above)
  // ============================================================================

  /**
   * @deprecated Use split endpoints instead: getMetadata, getGestures, getTranscription, getLandmarks, getBeliefs
   * Get complete analysis data for an interview
   * @param {number} interviewId - The interview ID
   * @returns {Promise} - Analysis data
   */
  getCompleteAnalysis(interviewId) {
    console.warn('[InterviewAnalysisDataService] getCompleteAnalysis is deprecated. Use split endpoints instead.');
    return http.get(API_URL_PATH + interviewId);
  }
}

export default new InterviewAnalysisDataService();
